import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { formatCurrency } from '@/lib/i18n';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Dialog } from '@/components/ui/dialog';
import { Field, Select } from '@/components/ui/field';
import { type Check, apiError, fromMinor, toMinor } from './lib';

/**
 * §9.2 — the three ways a check may be split. The API takes a discriminated
 * `mode`, so this dialog only ever sends the fields that mode owns.
 *
 * BY_PARTICIPANT is gone with Party. It grouped a check's charges by the person
 * each was posted for, and `Charge.participantId` — the column that recorded
 * that — no longer exists, so the grouping had nothing left to group on. BY_ITEM
 * answers the same question with the charges named explicitly, which is what
 * this dialog was sending for every other mode anyway.
 */
const MODES = ['BY_ITEM', 'EQUAL', 'PERCENTAGE'] as const;
type Mode = (typeof MODES)[number];

interface Props {
  check: Check;
  onClose: () => void;
  onDone: () => void;
}

export default function SplitDialog({ check, onClose, onDone }: Props) {
  const { t, i18n } = useTranslation();
  const [mode, setMode] = useState<Mode>('BY_ITEM');
  const [saving, setSaving] = useState(false);

  const liveCharges = useMemo(
    () => (check.charges ?? []).filter((c) => !c.voidedAt),
    [check.charges],
  );

  // Every figure on this dialog is in the check's own currency; splitting in
  // minor units keeps the shares exact, and the minor unit is 3 digits in KWD.
  const currency = check.currency;
  const grandMinor = toMinor(check.grandTotal, currency);

  // BY_ITEM — one bucket index per charge; buckets start at 1.
  const [buckets, setBuckets] = useState(2);
  const [assignment, setAssignment] = useState<Record<string, number>>({});

  // EQUAL
  const [ways, setWays] = useState(2);

  // PERCENTAGE
  const [shares, setShares] = useState<number[]>([50, 50]);

  /** Equal shares with the remainder landing on the last one, as the API does. */
  const equalPreview = useMemo(() => {
    if (ways < 2) return [];
    const each = Math.floor(grandMinor / ways);
    const parts = Array.from({ length: ways }, () => each);
    parts[parts.length - 1] += grandMinor - each * ways;
    return parts;
  }, [ways, grandMinor]);

  const percentTotal = shares.reduce((sum, value) => sum + value, 0);

  const submit = async () => {
    let body: Record<string, unknown>;
    if (mode === 'BY_ITEM') {
      const groups = Array.from({ length: buckets }, (_, index) =>
        liveCharges.filter((c) => (assignment[c.id] ?? 0) === index + 1).map((c) => c.id),
      )
        .filter((ids) => ids.length > 0)
        .map((chargeIds) => ({ chargeIds }));
      if (groups.length === 0) {
        toast.error(t('billing.split.assignSomething'));
        return;
      }
      body = { mode, groups };
    } else if (mode === 'EQUAL') {
      body = { mode, ways };
    } else if (mode === 'PERCENTAGE') {
      body = { mode, shares: shares.map((percent) => ({ percent })) };
    } else {
      body = { mode };
    }

    setSaving(true);
    try {
      await api.post(`/checks/${check.id}/split`, body);
      toast.success(t('billing.split.done'));
      onDone();
      onClose();
    } catch (e) {
      toast.error(apiError(e, t('billing.split.failed')));
    } finally {
      setSaving(false);
    }
  };

  const disabled =
    saving || (mode === 'PERCENTAGE' && Math.abs(percentTotal - 100) > 0.0001);

  return (
    <Dialog
      open
      onClose={onClose}
      title={t('billing.split.title', { number: check.number })}
      description={t('billing.split.subtitle')}
      className="max-w-2xl"
      footer={
        <>
          <Button variant="outline" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button onClick={submit} disabled={disabled}>
            {t('billing.split.confirm')}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <div className="grid gap-2 sm:grid-cols-2">
          {MODES.map((value) => (
            <button
              key={value}
              onClick={() => setMode(value)}
              className={`rounded-lg border p-3 text-start text-sm transition-colors ${
                mode === value ? 'border-accent bg-accent-soft' : 'hover:bg-hover'
              }`}
            >
              <div className="font-medium">{t(`billing.split.mode.${value}`)}</div>
              <p className="text-xs text-ink-muted">
                {t(`billing.split.modeHint.${value}`)}
              </p>
            </button>
          ))}
        </div>

        <div className="rounded-lg border p-4">
          {mode === 'BY_ITEM' && (
            <div className="space-y-3 text-sm">
              <div className="flex items-center gap-2">
                <Field label={t('billing.split.buckets')}>
                  <Input
                    className="w-24"
                    type="number"
                    min="2"
                    max="20"
                    value={buckets}
                    onChange={(e) => setBuckets(Math.max(2, Number(e.target.value) || 2))}
                  />
                </Field>
              </div>
              <ul className="divide-y rounded-lg border">
                {liveCharges.map((charge) => (
                  <li key={charge.id} className="flex items-center justify-between gap-3 p-2.5">
                    <span className="min-w-0 truncate">
                      {charge.description}
                      <span className="ms-2 text-ink-muted">
                        {formatCurrency(charge.amount, check.currency, i18n.language)}
                      </span>
                    </span>
                    <Select
                      className="w-32"
                      value={assignment[charge.id] ?? 0}
                      onChange={(e) =>
                        setAssignment({ ...assignment, [charge.id]: Number(e.target.value) })
                      }
                      aria-label={t('billing.split.bucketFor', { name: charge.description })}
                    >
                      <option value={0}>{t('billing.split.keepHere')}</option>
                      {Array.from({ length: buckets }, (_, index) => (
                        <option key={index} value={index + 1}>
                          {t('billing.split.bucket', { index: index + 1 })}
                        </option>
                      ))}
                    </Select>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {mode === 'EQUAL' && (
            <div className="space-y-3 text-sm">
              <Field label={t('billing.split.ways')} hint={t('billing.split.waysHint')}>
                <Input
                  className="w-24"
                  type="number"
                  min="2"
                  max="50"
                  value={ways}
                  onChange={(e) => setWays(Math.max(2, Number(e.target.value) || 2))}
                />
              </Field>
              <ul className="divide-y rounded-lg border">
                {equalPreview.map((minor, index) => (
                  <li key={index} className="flex justify-between gap-2 p-2.5">
                    <span>
                      {t('billing.split.share', { index: index + 1 })}
                      {index === equalPreview.length - 1 && (
                        <Badge tone="info" className="ms-2">
                          {t('billing.split.remainderHere')}
                        </Badge>
                      )}
                    </span>
                    <span>{formatCurrency(fromMinor(minor, currency), currency, i18n.language)}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {mode === 'PERCENTAGE' && (
            <div className="space-y-3 text-sm">
              <ul className="space-y-2">
                {shares.map((percent, index) => (
                  <li key={index} className="flex items-center gap-2">
                    <Input
                      className="w-24"
                      type="number"
                      min="0"
                      max="100"
                      step="0.01"
                      value={percent}
                      onChange={(e) =>
                        setShares(
                          shares.map((value, i) => (i === index ? Number(e.target.value) || 0 : value)),
                        )
                      }
                      aria-label={t('billing.split.share', { index: index + 1 })}
                    />
                    <span className="text-ink-muted">%</span>
                    <span className="ms-auto">
                      {formatCurrency(
                        fromMinor(Math.round((grandMinor * percent) / 100), currency),
                        currency,
                        i18n.language,
                      )}
                    </span>
                    {shares.length > 2 && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setShares(shares.filter((_, i) => i !== index))}
                      >
                        {t('common.delete')}
                      </Button>
                    )}
                  </li>
                ))}
              </ul>
              <div className="flex items-center gap-3">
                <Button size="sm" variant="outline" onClick={() => setShares([...shares, 0])}>
                  {t('billing.split.addShare')}
                </Button>
                <span
                  className={
                    Math.abs(percentTotal - 100) > 0.0001 ? 'text-red-600 dark:text-red-400' : 'text-green-700 dark:text-green-400'
                  }
                >
                  {t('billing.split.percentTotal', { total: percentTotal })}
                </span>
              </div>
            </div>
          )}
        </div>

        <p className="text-xs text-ink-muted">{t('billing.split.offsetNote')}</p>
      </div>
    </Dialog>
  );
}
